import { Link, useNavigate } from "react-router-dom";
import { Play, ArrowRight, ShieldCheck, Clock, Sparkles, Wrench, Flame, Trees, Home, Bug } from "lucide-react";
import Logo from "@/components/Logo";

const founderPoster =
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxwbHVtYmVyJTIwZnJpZW5kbHklMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODMyODAxODF8MA&ixlib=rb-4.1.0&q=85";
const coffeeImg =
  "https://images.unsplash.com/photo-1518057111178-44a106bad636?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxjb250cmFjdG9yJTIwbW9ybmluZyUyMGNvZmZlZXxlbnwwfHx8fDE3ODMyODAxODF8MA&ixlib=rb-4.1.0&q=85";
const truckImg =
  "https://customer-assets.emergentagent.com/job_growth-partner-118/artifacts/mfugtxv1_d2b37a11-bf8f-4500-87f0-3c34f3c2e6c6.png";

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: "var(--ta-bg)" }} data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur-md" style={{ background: "rgba(249,248,246,0.85)" }}>
        <div className="w-full max-w-screen-lg mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <Link
            to="/signup"
            data-testid="nav-signup-link"
            className="hidden sm:inline-flex items-center gap-2 h-11 px-5 rounded-full text-sm font-medium border-2 transition-transform hover:-translate-y-0.5"
            style={{ borderColor: "var(--ta-primary)", color: "var(--ta-primary)" }}
          >
            Sign in
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="w-full max-w-screen-lg mx-auto px-5 sm:px-8 pt-8 sm:pt-14 pb-6">
        <div className="stagger">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium mb-6"
            style={{ background: "var(--ta-accent)", color: "var(--ta-primary)" }}
            data-testid="hero-eyebrow"
          >
            <Sparkles size={14} strokeWidth={2.2} />
            A weekly brief. Never a dashboard to babysit.
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]"
            style={{ color: "var(--ta-primary)" }}
            data-testid="hero-headline"
          >
            Know exactly what your business needs this&nbsp;week.
          </h1>
          <p
            className="mt-5 sm:mt-6 text-lg sm:text-xl leading-relaxed max-w-xl"
            style={{ color: "var(--ta-text-secondary)" }}
            data-testid="hero-subheadline"
          >
            Become the obvious choice wherever customers look for you. One clear brief, every Monday morning — written like a trusted advisor, not a robot.
          </p>

          {/* CTAs */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-xl">
            <button
              onClick={() => navigate("/signup")}
              data-testid="primary-cta-button"
              className="inline-flex items-center justify-center gap-2 min-h-[64px] px-8 rounded-2xl text-lg font-medium text-white transition-transform hover:-translate-y-0.5 active:translate-y-0 shadow-sm"
              style={{ background: "var(--ta-primary)" }}
            >
              Start My Free Monday Brief
              <ArrowRight size={20} strokeWidth={2.4} />
            </button>
            <button
              onClick={() => navigate("/sample-brief")}
              data-testid="secondary-cta-button"
              className="inline-flex items-center justify-center min-h-[64px] px-8 rounded-2xl text-lg font-medium border-2 transition-transform hover:-translate-y-0.5"
              style={{ borderColor: "var(--ta-primary)", color: "var(--ta-primary)", background: "transparent" }}
            >
              See a Sample Monday Brief
            </button>
          </div>
          <p className="mt-4 text-sm inline-flex items-center gap-2" style={{ color: "var(--ta-text-secondary)" }} data-testid="no-cc-line">
            <ShieldCheck size={16} strokeWidth={2} style={{ color: "var(--ta-success)" }} />
            No credit card required. Cancel anytime.
          </p>
        </div>

        {/* Founder video placeholder */}
        <div className="mt-12 sm:mt-16" data-testid="founder-video-section">
          <div className="relative w-full aspect-[16/10] rounded-[28px] sm:rounded-[36px] overflow-hidden shadow-xl">
            <img
              src={founderPoster}
              alt="Founder welcome video"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(28,61,51,0.05) 0%, rgba(28,61,51,0.55) 100%)" }} />
            <button
              data-testid="founder-video-play-button"
              className="absolute inset-0 flex items-center justify-center group"
              aria-label="Play founder welcome video"
            >
              <span
                className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full shadow-2xl transition-transform group-hover:scale-105"
                style={{ background: "var(--ta-secondary)" }}
              >
                <Play size={34} strokeWidth={2.2} fill="#fff" color="#fff" />
              </span>
            </button>
            <div className="absolute left-6 bottom-6 sm:left-8 sm:bottom-8 max-w-xs">
              <div className="text-xs uppercase tracking-widest text-white/80 font-medium">A 90-second welcome</div>
              <div className="font-display text-white text-xl sm:text-2xl font-semibold mt-1">
                Why we built Your Monday Brief for shops like yours
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="w-full max-w-screen-lg mx-auto px-5 sm:px-8 mt-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          <TrustCard icon={ShieldCheck} title="No fear, no fluff" desc="If everything looks good, we say so. Trust is our only product." />
          <TrustCard icon={Clock} title="5 minutes a week" desc="One brief on Monday. Read it with your coffee. Get on with your day." />
          <TrustCard icon={Sparkles} title="Crystal Clear Brief" desc="Every recommendation is plain English. No jargon. No dashboards to babysit." />
        </div>
      </section>

      {/* How it works */}
      <section className="w-full max-w-screen-lg mx-auto px-5 sm:px-8 mt-20 sm:mt-28">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest font-medium mb-3" style={{ color: "var(--ta-secondary)" }}>How it works</div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: "var(--ta-primary)" }} data-testid="how-it-works-heading">
            Three things happen quietly in the background.
          </h2>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          <StepCard n="01" title="We watch your business" desc="Reviews, local search, website, listings — the places customers actually look." />
          <StepCard n="02" title="We think before we speak" desc="No red alerts at 3am. We wait, we consider, and we deliver one clear brief on Monday." />
          <StepCard n="03" title="You stay in control" desc="Handle it yourself, or tap one button and we'll take care of it for you." />
        </div>
      </section>

      {/* Trades we serve */}
      <section className="w-full max-w-screen-lg mx-auto px-5 sm:px-8 mt-20 sm:mt-28">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest font-medium mb-3" style={{ color: "var(--ta-secondary)" }}>Built for the trades</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: "var(--ta-primary)" }}>
              For the folks with muddy boots and full voicemails.
            </h2>
            <p className="mt-4 text-lg leading-relaxed" style={{ color: "var(--ta-text-secondary)" }}>
              We built Your Monday Brief for the shops we grew up around — plumbers, HVAC techs, electricians, roofers, and everyone in between.
            </p>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="trades-chips">
              {[
                { label: "Plumbing", icon: Wrench },
                { label: "HVAC", icon: Flame },
                { label: "Landscaping", icon: Trees },
                { label: "Roofing", icon: Home },
                { label: "Pest Control", icon: Bug },
              ].map((t) => (
                <span
                  key={t.label}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                  style={{ background: "var(--ta-surface-alt)", color: "var(--ta-primary)" }}
                >
                  <t.icon size={14} strokeWidth={2.2} /> {t.label}
                </span>
              ))}
            </div>
          </div>
          <div className="relative rounded-[32px] overflow-hidden aspect-[4/5] shadow-xl">
            <img src={truckImg} alt="Local plumber standing in front of a Quality Plumbing service van" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Sample brief tease */}
      <section className="w-full max-w-screen-lg mx-auto px-5 sm:px-8 mt-20 sm:mt-28">
        <div
          className="relative rounded-[36px] overflow-hidden p-8 sm:p-12"
          style={{ background: "var(--ta-primary)" }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-xs uppercase tracking-widest text-white/70 font-medium mb-3">Every Monday</div>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight">
                One brief. One coffee. That's it.
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-white/80 max-w-md">
                Peek at a real Monday Brief before you sign up. No form, no email — just take a look.
              </p>
              <Link
                to="/sample-brief"
                data-testid="sample-cta-bottom"
                className="mt-8 inline-flex items-center justify-center gap-2 min-h-[60px] px-7 rounded-2xl text-lg font-medium transition-transform hover:-translate-y-0.5"
                style={{ background: "var(--ta-secondary)", color: "#fff" }}
              >
                See a Sample Monday Brief <ArrowRight size={20} />
              </Link>
            </div>
            <div className="hidden md:block relative rounded-[24px] overflow-hidden aspect-[4/3]">
              <img src={coffeeImg} alt="Monday morning coffee" className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full max-w-screen-lg mx-auto px-5 sm:px-8 mt-24 pb-16">
        <div
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-8 border-t"
          style={{ borderColor: "var(--ta-border)" }}
        >
          <Logo />
          <p className="text-sm" style={{ color: "var(--ta-text-secondary)" }}>
            &copy; 2026 Your Monday Brief. Help first. The money follows.
          </p>
        </div>
        <div
          className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm"
          style={{ color: "var(--ta-text-secondary)" }}
        >
          <div className="flex flex-wrap gap-5">
            <Link
              to="/privacy"
              className="hover:underline"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="hover:underline"
              data-testid="footer-terms-link"
            >
              Terms of Service
            </Link>
          </div>
          <Link
            to="/admin/businesses"
            className="hover:underline"
            style={{ color: "var(--ta-text-secondary)" }}
            data-testid="footer-admin-login-link"
          >
            Admin sign in
          </Link>
        </div>
      </footer>
    </div>
  );
}

const TrustCard = ({ icon: Icon, title, desc }) => (
  <div
    className="p-6 rounded-3xl bg-white border"
    style={{ borderColor: "var(--ta-border)" }}
  >
    <div
      className="inline-flex items-center justify-center w-11 h-11 rounded-full mb-3"
      style={{ background: "var(--ta-accent)", color: "var(--ta-primary)" }}
    >
      <Icon size={20} strokeWidth={2.2} />
    </div>
    <div className="font-display font-semibold text-lg" style={{ color: "var(--ta-primary)" }}>
      {title}
    </div>
    <p className="mt-1 text-base leading-relaxed" style={{ color: "var(--ta-text-secondary)" }}>
      {desc}
    </p>
  </div>
);

const StepCard = ({ n, title, desc }) => (
  <div className="p-6 sm:p-8 rounded-[28px] bg-white border" style={{ borderColor: "var(--ta-border)" }}>
    <div className="font-display text-3xl font-semibold" style={{ color: "var(--ta-secondary)" }}>{n}</div>
    <div className="mt-3 font-display text-xl font-semibold" style={{ color: "var(--ta-primary)" }}>{title}</div>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--ta-text-secondary)" }}>{desc}</p>
  </div>
);
